import React from 'react';

import { Link } from '~/lib/react-router-dom';

import { Link as MuiLink } from '@mui/material';

import {
  type MarketingRouterTreatmentType,
  getMarketingRouterTreatmentConfig,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

type Treatment = {
  type: MarketingRouterTreatmentType;
  channelMarkupId?: string;
  promoDisplayConfigId?: string;
  voucherDisplayConfigId?: string;
};

/**
 * The admin page for the record a treatment points at, or undefined when the
 * treatment carries no linkable id.
 *
 * The promo display config list only opens `editingConfigId` when the config is
 * on the loaded page, so widen the list to every status and a large page.
 */
export function getTreatmentLinkPath(treatment: Treatment): string | undefined {
  if (treatment.type === 'channel-markup' && treatment.channelMarkupId) {
    return `/marketing/channel-markup-v2/${encodeURIComponent(treatment.channelMarkupId)}/edit`;
  }

  if (treatment.type === 'promo-display-config' && treatment.promoDisplayConfigId) {
    const params = new URLSearchParams({
      page_num: '0',
      page_size: '100',
      status: 'all',
      source: 'ALL',
      medium: 'ALL',
      campaign: 'ALL',
      editingConfigId: treatment.promoDisplayConfigId,
    });
    return `/marketing/promo-display-config?${params.toString()}`;
  }

  if (treatment.type === 'voucher-display-config' && treatment.voucherDisplayConfigId) {
    return `/marketing/voucher-display-config/${encodeURIComponent(treatment.voucherDisplayConfigId)}`;
  }

  return undefined;
}

export function formatTreatments(treatments?: Array<Treatment>): React.ReactNode {
  if (!treatments?.length) {
    return '—';
  }

  return treatments.map((treatment, index) => {
    const treatmentName = getMarketingRouterTreatmentConfig(treatment.type)?.typeName ?? treatment.type;
    const linkPath = getTreatmentLinkPath(treatment);
    const treatmentLabel = linkPath ? (
      <MuiLink component={Link} to={linkPath} underline="hover">
        {treatmentName}
      </MuiLink>
    ) : (
      treatmentName
    );

    return (
      <React.Fragment
        key={`${treatment.type}-${
          treatment.channelMarkupId ?? treatment.promoDisplayConfigId ?? treatment.voucherDisplayConfigId ?? index
        }`}
      >
        {index > 0 ? ', ' : null}
        {treatmentLabel}
      </React.Fragment>
    );
  });
}
